import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sun, Moon, Bell, Search, Menu, ChevronDown,
  User, Settings, CreditCard, LogOut, Shield,
  Building2, CheckCircle, Clock, AlertCircle, Zap,
  Users, Star, Award, Calendar, GraduationCap, Trophy,
  ExternalLink, CheckCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useTheme } from '@/context/ThemeContext';
import { useApp } from '@/context/AppContext';
import { useSTNAuth } from '@/context/STNAuthContext';
import { cn } from '@/lib/utils';

// ── Notification type configs (matches Notifications page) ────────────────────
const N_TYPE = {
  application:   { icon: Users,         color: 'text-chart-1' },
  milestone:     { icon: Star,          color: 'text-chart-4' },
  payment:       { icon: CreditCard,    color: 'text-chart-3' },
  match:         { icon: Zap,           color: 'text-primary' },
  certificate:   { icon: Award,         color: 'text-chart-5' },
  event:         { icon: Calendar,      color: 'text-chart-2' },
  mentor:        { icon: GraduationCap, color: 'text-warning' },
  gamification:  { icon: Trophy,        color: 'text-warning' },
  change_request:{ icon: Shield,        color: 'text-chart-1' },
  system:        { icon: Bell,          color: 'text-muted-foreground' },
};

function relTime(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60)  return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// ── Notification dropdown panel ───────────────────────────────────────────────
const NotificationDropdown = ({ onClose }) => {
  const navigate = useNavigate();
  const { notifications, unreadCount, markRead, markAllRead } = useApp();
  const preview = notifications.slice(0, 6);

  const handleClick = (notif) => {
    const id = notif._id || notif.id;
    markRead(id);
    onClose();
    if (notif.link) {
      if (notif.link.startsWith('http')) window.open(notif.link, '_blank');
      else navigate(notif.link);
    }
  };

  return (
    <div className="w-80 max-h-[480px] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-primary" />
          <span className="font-semibold text-foreground text-sm">Notifications</span>
          {unreadCount > 0 && (
            <span className="bg-primary text-primary-foreground text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); markAllRead(); }}
            className="text-[11px] text-primary hover:underline flex items-center gap-1"
          >
            <CheckCheck className="w-3 h-3" /> All read
          </button>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {preview.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
            No notifications yet
          </div>
        ) : (
          preview.map(notif => {
            const id      = notif._id || notif.id;
            const ncfg    = N_TYPE[notif.type] || N_TYPE.system;
            const Icon    = ncfg.icon;
            return (
              <button
                key={id}
                onClick={() => handleClick(notif)}
                className={cn(
                  'w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-muted/60 transition-colors border-b border-border/40 last:border-0',
                  !notif.read && 'bg-primary/4'
                )}
              >
                <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 bg-muted')}>
                  <Icon className={cn('w-3.5 h-3.5', ncfg.color)} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className={cn('text-xs font-semibold truncate', !notif.read ? 'text-foreground' : 'text-muted-foreground')}>
                      {notif.title}
                    </p>
                    {!notif.read && <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />}
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate mt-0.5">{notif.message}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-[10px] text-muted-foreground/60">{relTime(notif.createdAt || notif.time)}</span>
                    {notif.link && (
                      <span className="text-[10px] text-primary flex items-center gap-0.5">
                        <ExternalLink className="w-2.5 h-2.5" /> {notif.linkLabel || 'Open'}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-border px-4 py-2.5">
        <button
          onClick={() => { onClose(); navigate('/notifications'); }}
          className="w-full text-center text-xs font-medium text-primary hover:underline py-0.5"
        >
          View all notifications
        </button>
      </div>
    </div>
  );
};

// ── Status badge config ───────────────────────────────────────────────────────
const STATUS_CONFIG = {
  verified:       { label: 'Verified',      icon: CheckCircle, className: 'bg-success/15 text-success border-success/30' },
  pending_review: { label: 'Under Review',  icon: Clock,       className: 'bg-warning/15 text-warning border-warning/30' },
  rejected:       { label: 'Action Needed', icon: AlertCircle, className: 'bg-destructive/15 text-destructive border-destructive/30' },
  draft:          { label: 'Free Plan',     icon: Zap,         className: 'bg-muted text-muted-foreground border-border' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const getInitials = (name = '') =>
  name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '??';

const getAvatarGradient = (name = '') => {
  const colors = [
    'from-violet-500 to-purple-600',
    'from-blue-500 to-cyan-600',
    'from-emerald-500 to-teal-600',
    'from-orange-500 to-amber-600',
    'from-rose-500 to-pink-600',
    'from-indigo-500 to-blue-600',
  ];
  const idx = name.charCodeAt(0) % colors.length;
  return colors[idx] || colors[0];
};

export const TopBar = ({ onMenuClick }) => {
  const { theme, toggleTheme } = useTheme();
  const { unreadCount, currentPlan } = useApp();
  const { founder, startup, isAuthenticated, logout } = useSTNAuth();
  const [notifOpen, setNotifOpen] = useState(false);
  const navigate  = useNavigate();
  const [searchFocused, setSearchFocused] = useState(false);
  const searchRef = useRef(null);

  // ── Derive display values from real auth data ─────────────────────────────
  const name        = founder?.name        || 'Guest User';
  const email       = founder?.email       || '';
  const role        = founder?.role        || 'Founder';
  const companyName = startup?.companyName || 'Your Startup';
  const stnId       = founder?.stnId       || null;
  const startupId   = startup?.stnId       || null;
  const status      = startup?.status      || 'draft';
  const initials    = getInitials(name);
  const gradient    = getAvatarGradient(name);

  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const StatusIcon = statusCfg.icon;

  // Plan label: use plan from billing context; fall back to startup status label
  const planLabel = status === 'verified'
    ? currentPlan !== 'free' ? `${currentPlan} plan` : 'Verified'
    : statusCfg.label;

  // ── Logout ────────────────────────────────────────────────────────────────
  const handleLogout = () => {
    if (isAuthenticated) logout();
    navigate('/login');
  };

  // ── Keyboard shortcut: "/" focuses search ─────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <header className="h-16 border-b border-border bg-card/80 backdrop-blur-sm flex items-center gap-3 px-4 lg:px-6 sticky top-0 z-40">

      {/* Menu toggle */}
      <button
        onClick={onMenuClick}
        className="p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground flex-shrink-0"
      >
        <Menu className="w-4 h-4" />
      </button>

      {/* Search */}
      <div className={cn(
        'relative max-w-xs w-full transition-all duration-200',
        searchFocused ? 'max-w-sm' : 'max-w-xs'
      )}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <Input
          ref={searchRef}
          placeholder="Search roles, candidates, events…"
          className="pl-8 pr-8 h-9 bg-muted/50 border-transparent focus:border-border text-sm"
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
        />
        <kbd className={cn(
          'absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground/60 font-mono transition-opacity',
          searchFocused ? 'opacity-0' : 'opacity-100'
        )}>/</kbd>
      </div>

      <div className="flex-1" />

      {/* Startup status badge — desktop only */}
      {isAuthenticated && companyName && (
        <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-muted/50 border border-border max-w-[180px]">
          <Building2 className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          <span className="text-xs text-foreground font-medium truncate">{companyName}</span>
        </div>
      )}

      {/* Verification status badge */}
      {isAuthenticated && (
        <Badge
          variant="outline"
          className={cn('hidden sm:flex items-center gap-1 text-xs px-2 py-0.5 font-medium flex-shrink-0', statusCfg.className)}
        >
          <StatusIcon className="w-3 h-3" />
          {statusCfg.label}
        </Badge>
      )}

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className="p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground flex-shrink-0"
        title="Toggle theme"
      >
        {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>

      {/* Notifications dropdown */}
      <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
        <DropdownMenuTrigger asChild>
          <button
            className="relative p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground flex-shrink-0"
            title="Notifications"
          >
            <Bell className="w-4 h-4" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center leading-none animate-pulse">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="p-0 overflow-hidden" sideOffset={8}>
          <NotificationDropdown onClose={() => setNotifOpen(false)} />
        </DropdownMenuContent>
      </DropdownMenu>

      {/* User dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-lg hover:bg-accent transition-colors flex-shrink-0 outline-none">
            <Avatar className="w-7 h-7 flex-shrink-0">
              <AvatarFallback className={cn('bg-gradient-to-br text-white text-xs font-bold', gradient)}>
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="text-left hidden sm:block min-w-0">
              <p className="text-sm font-semibold text-foreground leading-tight truncate max-w-[120px]">{name}</p>
              <p className="text-xs text-muted-foreground leading-tight capitalize">{planLabel}</p>
            </div>
            <ChevronDown className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-64">
          {/* Identity block */}
          <div className="px-3 py-3 border-b border-border">
            <div className="flex items-center gap-3 mb-2">
              <Avatar className="w-10 h-10 flex-shrink-0">
                <AvatarFallback className={cn('bg-gradient-to-br text-white text-sm font-bold', gradient)}>
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{name}</p>
                <p className="text-xs text-muted-foreground truncate">{email}</p>
                <p className="text-xs text-muted-foreground capitalize">{role}</p>
              </div>
            </div>

            {/* Company + status row */}
            <div className="flex items-center justify-between gap-2 mt-1">
              <div className="flex items-center gap-1.5 min-w-0">
                <Building2 className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                <span className="text-xs text-muted-foreground truncate">{companyName}</span>
              </div>
              <Badge
                variant="outline"
                className={cn('text-[10px] px-1.5 py-0 font-medium flex items-center gap-1 flex-shrink-0', statusCfg.className)}
              >
                <StatusIcon className="w-2.5 h-2.5" />
                {statusCfg.label}
              </Badge>
            </div>

            {/* STN IDs */}
            {(stnId || startupId) && (
              <div className="mt-2 pt-2 border-t border-border/50 space-y-0.5">
                {stnId && (
                  <p className="text-[10px] font-mono text-muted-foreground/70">
                    <span className="text-muted-foreground">Founder ID: </span>{stnId}
                  </p>
                )}
                {startupId && (
                  <p className="text-[10px] font-mono text-muted-foreground/70">
                    <span className="text-muted-foreground">Startup ID: </span>{startupId}
                  </p>
                )}
              </div>
            )}
          </div>

          <DropdownMenuItem onClick={() => navigate('/settings')} className="gap-2">
            <User className="w-4 h-4" /> Profile
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/settings')} className="gap-2">
            <Settings className="w-4 h-4" /> Settings
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/billing')} className="gap-2">
            <CreditCard className="w-4 h-4" /> Billing & Plan
          </DropdownMenuItem>
          {status !== 'verified' && (
            <DropdownMenuItem onClick={() => navigate('/onboarding')} className="gap-2 text-primary">
              <Shield className="w-4 h-4" /> Complete Verification
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-destructive gap-2" onClick={handleLogout}>
            <LogOut className="w-4 h-4" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
};
